// NEVANU — Auth & Session Management
// Zentralisiertes User/Session-Handling

import type { Session } from './types';
import { rolleBezeichnung } from './rollen';

const SESSION_KEY = 'nevanuSession';
const USER_KEY = 'currentUser';

/** Aktuelle Session aus localStorage lesen */
export function getSession(): Session | null {
  try {
    // Neues Format: nevanuSession
    const raw = localStorage.getItem(SESSION_KEY);
    if (raw) return JSON.parse(raw);
    // Fallback: Legacy form.js Format (nevanu_session)
    const legacy = localStorage.getItem('nevanu_session');
    if (legacy) {
      const parsed = JSON.parse(legacy);
      if (parsed?.user?.name) {
        // Legacy format: { user: { name, pin, role, email, id }, timestamp }
        return { user: parsed.user.name, pin: parsed.user.pin, role: parsed.user.role, email: parsed.user.email || '', userId: parsed.user.id };
      }
    }
    return null;
  } catch {
    return null;
  }
}

/** Session speichern */
export function saveSession(session: Session): void {
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  localStorage.setItem(USER_KEY, session.user);
}

/** Session löschen (Logout) */
export function clearSession(): void {
  localStorage.removeItem(SESSION_KEY);
  localStorage.removeItem(USER_KEY);
  // B12 (Strang 2, 09.10.2026): beim Abmelden die Offline-Kopie löschen (src/offline/db.ts offlineLoeschen über window —
  // kein Import, damit auth.ts ohne IndexedDB testbar bleibt)
  try { (window as any).nevanuOffline?.loeschen?.(); } catch { /* egal */ }
}

/** Ist User eingeloggt? */
export function isLoggedIn(): boolean {
  return getSession() !== null;
}

/** Aktuellen User-Name holen */
export function getCurrentUser(): string {
  return localStorage.getItem(USER_KEY) || '';
}

/** Aktuelle Rolle */
export function getCurrentRole(): string {
  const session = getSession();
  return session?.role || '';
}

/** Ist der aktuelle User Admin/Inhaber? */
export function isAdmin(): boolean {
  const role = getCurrentRole();
  return role === 'admin' || role === 'inhaber';
}

/** Ist der aktuelle User Manager (inkl. Admin/Inhaber)? */
export function isManager(): boolean {
  const role = getCurrentRole();
  return ['admin', 'inhaber', 'salon_manager', 'filialleiter'].includes(role);
}

/** Rollen-Label für Anzeige — C10 (S2, 10.10.2026): aus der einen Quelle (src/core/rollen.ts → js/form.js →
 *  Tabelle `rollen`); vorher eine eigene Liste mit Schlüsseln, die es serverseitig nicht gibt (senior_stylist …). */
export function getRoleLabel(role: string): string {
  return rolleBezeichnung(role);
}
